export const weijuHero = {
  eyebrow: '客户案例 · 敬龙国际',
  title: '味聚',
  titleHighlight: '餐饮消费与供应链数字化服务平台',
  subtitle: '让餐饮消费更简单，让商家经营更高效，让供应链连接更紧密',
  note: '一端连接消费者，一端连接餐饮商家，一端连接供应链，以数字化能力贯通消费、经营与产业。',
  primaryLabel: '了解核心业务',
  secondaryLabel: '看业务闭环',
  tags: ['聚餐社交', '餐饮经营', '甄选商城', '供应链协同', '数字权益', '平台治理'],
};

export const weijuIntro = {
  title: '味聚是什么',
  paragraphs: [
    '味聚是一套连接消费者、餐饮商家、供应商及平台运营方的综合服务平台，通过聚餐社交、餐饮消费、商品采购、供应链协同、数字权益及平台治理等能力，打通从用户消费到商家经营、从商品供应到交易履约的完整业务链路。',
    '平台以消费者需求为入口，以餐饮场景为核心，以供应链为支撑，通过数字化系统连接人、店、货、场、订单、权益和服务，形成餐饮消费与产业供应链协同发展的数字化生态。',
  ],
  positioning: '一端连接消费者，一端连接餐饮商家，一端连接供应链',
};

export interface ProblemGroup {
  role: string;
  icon: string;
  desc: string;
  items: string[];
}

export const weijuProblems: ProblemGroup[] = [
  {
    role: '消费者',
    icon: 'ri-user-heart-line',
    desc: '找餐厅、找套餐、组局聚餐效率低，消费优惠和权益分散，聚餐、支付、核销缺乏统一管理。',
    items: ['找餐厅、找套餐、组局聚餐效率低', '消费优惠和权益分散', '聚餐、支付、核销缺乏统一管理'],
  },
  {
    role: '餐饮商家',
    icon: 'ri-store-2-line',
    desc: '门店、菜品、订单、收银、库存各自独立，后厨、采购、员工、客户管理复杂，经营数据与财务结算难统一。',
    items: ['门店、菜品、订单、收银、库存各自独立', '后厨、采购、员工、客户管理复杂', '经营数据与财务结算难统一'],
  },
  {
    role: '供应商',
    icon: 'ri-truck-line',
    desc: '商品、库存、订单、配送信息分散，餐厅客户管理效率低，履约、质量追溯和结算缺乏统一平台。',
    items: ['商品、库存、订单、配送信息分散', '餐厅客户管理效率低', '履约、质量追溯和结算缺乏统一平台'],
  },
  {
    role: '平台',
    icon: 'ri-database-2-line',
    desc: '用户、商家、商品、订单、权益、财务数据难以统一治理。',
    items: ['用户、商家、商品、订单、权益、财务数据难以统一治理'],
  },
];

export interface Connection {
  name: string;
  icon: string;
  subtitle: string;
  points: string[];
}

export const weijuConnections: Connection[] = [
  {
    name: '消费者端',
    icon: 'ri-user-heart-line',
    subtitle: '聚餐交友 / V聚',
    points: ['AI 找餐厅 / 套餐', '邀请 / 报名 / 支付', 'V券 / 权益 / 核销'],
  },
  {
    name: '商家端',
    icon: 'ri-store-2-line',
    subtitle: '餐厅经营工作台',
    points: ['菜品 / 订单 / 收银', '后厨 / 库存 / 采购', '客户 / 员工 / 结算'],
  },
  {
    name: '供应链端',
    icon: 'ri-truck-line',
    subtitle: '供应商工作台',
    points: ['商品 / SPU / SKU', '库存 / 配送 / 履约', '溯源 / 结算 / 对账'],
  },
];

export interface BusinessModule {
  no: string;
  title: string;
  subtitle: string;
  flow: string[];
  points: string[];
  value: string;
}

export const weijuModules: BusinessModule[] = [
  {
    no: '01',
    title: '聚餐社交',
    subtitle: '让「吃饭」变成可组织的消费场景',
    flow: ['AI定制聚餐', '推荐餐厅/套餐', '发起聚餐', '邀请好友', '报名成局', '支付', '到店核销', '用餐评价'],
    points: ['V券', '新人权益', '身份成长体系'],
    value: '以「聚餐」连接人与人，以消费连接餐厅，以权益提升用户活跃度。',
  },
  {
    no: '02',
    title: '餐饮经营',
    subtitle: '帮助餐厅实现数字化经营',
    flow: ['入驻配置', '门店运营', '商品管理', '订单收银', '到店核销', '厨房出品', '库存采购', '客户经营', '对账结算'],
    points: ['门店', '菜品', '套餐', '收银', '订单', '核销', '后厨', '库存', '采购', '客户', '员工', '财务结算'],
    value: '一个工作台管理一家餐厅，让餐厅经营从「分散管理」走向「一体化数字经营」。',
  },
  {
    no: '03',
    title: '甄选商城',
    subtitle: '连接商品与消费需求',
    flow: ['搜索商品', '查看供应商', '加入购物车', '下单', '配送', '收货', '评价/售后'],
    points: ['精选食材', '工厂直供', '进口食品', '农产品及相关商品', '供应商店铺', '卡券', '采购需求'],
    value: '从「餐桌消费」延伸到「食材采购」，打通消费端与供应端。',
  },
  {
    no: '04',
    title: '供应链协同',
    subtitle: '让商品从供应商直达餐桌',
    flow: ['主体准入', '合同授权', '商品发布', '审核', '客户下单', '出库', '配送', '收货', '库存溯源', '对账结算'],
    points: ['SPU / SKU商品管理', '餐厅客户管理', '库存及仓储', '批次管理', '温控记录', '配送履约', '售后及召回', '财务对账', '商品质量追溯'],
    value: '连接餐厅与供应商，让商品、库存、订单、配送和结算在线协同。',
  },
  {
    no: '05',
    title: '数字权益',
    subtitle: '让消费具备持续连接能力',
    flow: ['创建', '发行', '领取', '激活', '使用', '核销', '过期/撤回'],
    points: ['V券', '新人权益', '卡券', '活动权益', '身份体系（V客 / V使 / V创 / V代）'],
    value: '将一次消费转化为持续用户关系，实现从消费者到平台生态参与者的身份成长。',
  },
  {
    no: '06',
    title: '平台治理',
    subtitle: '保障业务可信运行',
    flow: ['统一用户与身份', '商品SPU/SKU治理', '订单履约管理', '库存物流管理', '财务结算', '权益运营', '风控审计', 'AI匹配', '数据分析', '系统安全'],
    points: ['用户', '组织', '餐厅', '供应商', '商品', '交易', '库存', '财务', '权益', '风控', '客服', '数据', '系统安全'],
    value: '实现平台业务的统一管理和持续运营。',
  },
];

export interface CoreValue {
  no: string;
  title: string;
  desc: string;
}

export const weijuCoreValues: CoreValue[] = [
  {
    no: '01',
    title: '连接消费者',
    desc: '从单一「吃饭消费」升级为：找餐厅 → 找套餐 → 找朋友 → 组聚餐 → 消费 → 分享',
  },
  {
    no: '02',
    title: '赋能餐厅',
    desc: '从传统门店管理升级为：门店数字化 + 商品数字化 + 订单数字化 + 库存数字化 + 客户数字化 + 财务数字化',
  },
  {
    no: '03',
    title: '连接供应链',
    desc: '从传统采购关系升级为：商品数字化 + 供应商数字化 + 履约在线化 + 质量可追溯',
  },
  {
    no: '04',
    title: '沉淀用户权益',
    desc: '通过 V券、卡券、活动、身份成长等机制，让消费行为形成持续的用户连接。',
  },
  {
    no: '05',
    title: '实现产业协同',
    desc: '将原本分散的人、店、货、场、订单、权益、供应链，统一连接到一个数字化平台。',
  },
];

export const weijuClosing = {
  title: '味聚一句话介绍',
  oneLiner: '味聚，是连接消费者、餐饮商家与供应链的数字化餐饮服务平台。',
  paragraphs: [
    '以「聚餐社交 + 餐饮消费」为用户入口，以餐厅经营和供应链协同为业务支撑，通过数字权益、订单履约、商品采购、数据与 AI 等能力，打通「人—店—货—场—交易」全链路，构建餐饮消费与产业供应链融合的数字化生态。',
  ],
  positioningTitle: '网站定位',
  positioningHighlight: '「餐饮消费 + 聚餐社交 + 供应链」三位一体的数字化平台',
  positioningDesc: '一端连接消费者，一端连接餐饮商家，一端连接供应链，以数字化能力贯通消费、经营与产业。',
  fivePoints: [
    { label: '聚餐', desc: 'AI 组局，让吃饭成为一种社交方式。' },
    { label: '消费', desc: '餐厅、套餐、优惠权益一站式连接。' },
    { label: '经营', desc: '覆盖餐厅从收银到后厨、库存、客户和结算的全流程。' },
    { label: '供应', desc: '连接供应商、商品、采购、库存、配送与溯源。' },
    { label: '生态', desc: '以用户为中心，连接人、店、货、场，形成餐饮产业数字化生态。' },
  ],
};